import { Link } from 'react-router-dom';
import { Eyebrow, SectionHeader } from '@/components/Atoms';
import { HALLIE } from '@/data/images';
import { WhyClientsBlock } from '@/components/Blocks';
import useSEO from '@/hooks/useSEO';
import { PERSON_SCHEMA } from '@/data/schema';

const VALUES = [
  { title: 'Reliability', body: 'Showing up consistently and following through is the foundation of everything I do.' },
  { title: 'Ownership', body: 'I take responsibility for outcomes, not just tasks.' },
  { title: 'Clarity', body: 'Clear communication, written updates, and no guessing where things stand.' },
  { title: 'Care', body: 'Your business and your clients are treated with the same attention I would give my own.' },
  { title: 'Structure', body: 'Calm, organised systems that make day-to-day operations easier.' },
  { title: 'Discretion', body: 'I work quietly behind the scenes. Your business stays yours.' },
];

const EXPERTISE = [
  'Operations management',
  'Executive support',
  'Customer success & retention',
  'Recruitment & team support',
  'CRM administration',
  'Process optimisation & automation',
  'Website strategy & development',
  'Local & on-page SEO',
  'Social media & brand presence',
  'Business development support',
];

export default function About() {
  useSEO({
    title: 'About Hallie — Operations & Systems Specialist',
    description:
      'Meet Hallie — Operations & Systems Specialist with 15+ years of experience helping service-based businesses improve efficiency, strengthen customer relationships, and build scalable operational processes.',
    image: HALLIE.power,
    path: '/about',
    schema: PERSON_SCHEMA,
  });

  return (
    <div>
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-7 reveal">
            <Eyebrow>About Hallie</Eyebrow>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight mt-6">
              Meet Hallie.
            </h1>
            <p className="font-serif-italic italic text-2xl sm:text-3xl text-[#C9A24D] mt-4 leading-snug">
              Operations & Systems Specialist.
            </p>
            <div className="mt-10 space-y-6 text-lg text-[#1C1C1C]/80 leading-relaxed max-w-2xl">
              <p>
                I&rsquo;m an Operations & Systems Specialist with more than 15 years of experience helping businesses improve efficiency, strengthen customer relationships, and build scalable operational processes.
              </p>
              <p>
                Throughout my career, I have supported organizations across the travel, customer service, and service-based industries — helping leaders streamline workflows, improve communication, implement technology solutions, and create systems that support sustainable growth.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5 reveal reveal-delay-2">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={HALLIE.power} alt="Hallie portrait" className="w-full h-full object-cover editorial-img" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Professional Background</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
              Fifteen years<br/>
              <span className="font-serif-italic italic text-[#C9A24D]">in operations and customer experience.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#F4EFEA]/85 leading-relaxed text-lg">
            <p>
              My work spans operations management, executive support, customer success, recruitment, CRM administration, process optimization, automation, and business development.
            </p>
            <p>
              I enjoy solving operational challenges, building structure where it is needed, and helping business owners gain confidence that the right systems and support are in place so they can focus on growth.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow="Areas of Expertise" title="Where I do" italic="my best work." />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#E6CFC7]/60">
            {EXPERTISE.map((e, i) => (
              <div key={e} className="bg-[#F4EFEA] p-8 hover:bg-[#FBF7F2] transition-colors">
                <div className="num-marker">{String(i + 1).padStart(2, '0')}</div>
                <p className="mt-4 font-medium text-[#1C1C1C] text-sm leading-snug">{e}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FBF7F2] border-y border-[#E6CFC7]/70 py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Approach</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
              Calm, structured,<br/>
              <span className="font-serif-italic italic text-[#C9A24D]">dependable.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#1C1C1C]/85 leading-relaxed text-lg">
            <p>
              My approach is straightforward. Understand the business, listen carefully, and build the structure that lets the day-to-day run with less friction.
            </p>
            <p>
              I favour fewer tools, clearer ownership, and written communication that the team can actually use. The goal is always the same: a business that runs better, with less mental load on the people leading it.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow="Values" title="Six principles" italic="that guide every engagement." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="card-soft p-8">
                <div className="hairline-gold" />
                <h3 className="font-serif-display text-2xl mt-5">{v.title}</h3>
                <p className="text-[#1C1C1C]/75 leading-relaxed mt-3">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <WhyClientsBlock />

      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 text-center">
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Curious whether<br/>
            <span className="font-serif-italic italic text-[#C9A24D]">we&rsquo;re the right fit?</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-ghost-light" data-testid="about-cta-book-call">Book a Discovery Call</Link>
            <Link to="/contact" className="text-sm uppercase tracking-[0.18em] text-[#F4EFEA]/70 hover:text-[#C9A24D] self-center transition-colors" data-testid="about-cta-consult">Request a Consultation &rarr;</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
